<template lang="pug">
.property-competitors
  .container-fluid.header-filters
    .container
      .saving-backdrop.text-center(v-if="loading")
        i.fas.fa-spinner.fa-spin.fa-4x
      .container
        .row.py-3
          .col
            h3.title-path
              router-link(:to="{ name: 'PropertyPage', params: property.id }") {{ $t("property_page.rent_assessment") }}
              span#arrow-delimiter >
              span {{ $t("competitors_page.title") }}
        .row.pb-3
          .col-12.col-md-11
            competitors-filter(:filters="filters" @change="applyFilters")
          .col-12.col-md-1
            print-competitors.page-print-version(
              :competitorsCount="pagination.total"
              @printCompetitor="printData"
            )

  .container-fluid
    .row
      .container
        .col-12
          .row.py-2.bordered-row
            .col
              property-info(v-if="!isEmpty(property) && !isEmpty(room)", :property="property", :room="room")

  .container-fluid
    .container
      .row
        .col-12
          competitors-map(
            v-if="isNotEmptyObjects"
            :property="property"
            :filters="filters"
            :key="uniqKey"
            :sortingOrder="sortingOrder"
          )

  .container-fluid
    .container
      .row.py-3.bordered-row
        .col.action-row
          h1.float-left.mb-0 {{ $t("competitors_page.title") }}
          .float-right
            b-button.btn-action.btn-download.mr-3(
              v-show="activeTab == 'list'"
              @click="downloadCompetitors"
            ) {{ $t("competitors_page.download") }}

            b-button-group
              b-button.btn-action(
                @click="activeTab = 'prices'"
                :class="{ selected: (activeTab == 'prices') }"
              ) {{ $t("competitors_page.market_prices") }}
              b-button.btn-action(
                @click="activeTab = 'list'"
                :class="{ selected: (activeTab == 'list') }"
              ) {{ $t("competitors_page.competitors") }}

      .row(v-show="activeTab == 'list'")
        .col
          .loader.text-center(v-if="loading")
            i.fas.fa-spinner.fa-spin.fa-2x
          competitors-list(
            v-else
            :competitors="competitors"
            :tableHeight="tableHeight"
            :sortingOrder="sortingOrder"
            :competitorRow="competitorRow"
            :printMode="printCompetitorsMode"
            @sorting="sortingCompetitors"
            @findCompetitor="findCompetitor"
          )

      .row(v-show="activeTab == 'prices'")
        .col
          .loader.text-center(v-if="loading")
            i.fas.fa-spinner.fa-spin.fa-2x
          market-prices(
            v-if="!isEmpty(room) && !loading"
            :competitors="competitorsForMarketPrice"
            :room="room"
            :marketPriceHeight="marketPriceHeight"
          )
      .row.pagination-block(v-show="!loading")
        .col
          pagination-wrapper(:pagination="pagination")
            vuetable-pagination(
              ref="pagination"
              :css="css.pagination"
              @vuetable-pagination:change-page="paginate"
            )
</template>

<script>
import { api } from '@/api'
import { isEmpty } from 'lodash'
// TODO: uncomment and replace "lodash" import after unblock grouping competitors card
// import { isEmpty, uniq, filter, flatten } from 'lodash'
import CompetitorsFilter from '@/pages/Property/Competitors/filter.vue'
import PropertyInfo from '@/pages/Property/Competitors/property-info.vue'
import CompetitorsList from '@/pages/Property/Competitors/competitors-list.vue'
import MarketPrices from '@/pages/Property/Competitors/market-prices.vue'
import CompetitorsMap from '@/pages/Property/Competitors/map.vue'
import downloadXlsx from '@/mixins/download-xlsx'
import PaginationWrapper from '@/components/shared/pagination-wrapper'
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination'
import PrintCompetitors from '@/pages/Property/Competitors/print-competitors.vue'

export default {
  components: {
    CompetitorsFilter,
    PropertyInfo,
    CompetitorsList,
    MarketPrices,
    CompetitorsMap,
    VuetablePagination,
    PaginationWrapper,
    PrintCompetitors
  },

  mixins: [
    downloadXlsx
  ],

  data() {
    return {
      property: {},
      room: {},
      activeTab: 'list',
      filters: {
        radius: 500,
        layout: [],
        age: [],
        area: {},
        rentFee: {},
        basicUnitPrice: [],
        timeNearestStation: [],
        buildingStructure: [],
        otaSite: [],
        facilities: []
      },
      pagination: {
        total: 0,
        per_page: 30,
        current_page: 1,
        last_page: 1
      },
      competitors: [],
      competitorsForMarketPrice: [],
      uniqKey: 1,
      tableHeight: '600px',
      marketPriceHeight: '500px',
      loading: true,
      css: {
        pagination: {
          wrapperClass: 'vuetable-pagination',
          activeClass: 'btn pagination-button active',
          disabledClass: 'disabled',
          pageClass: 'btn pagination-button',
          linkClass: 'btn pagination-button',
          icons: {
            first: 'fas fa-angle-double-left',
            prev: 'fas fa-angle-left',
            next: 'fas fa-angle-right',
            last: 'fas fa-angle-double-right'
          }
        }
      },
      sortingOrder: {
        sortField: 'distance',
        direction: 'asc'
      },
      competitorRow: -1,
      printCompetitorsMode: false
    }
  },
  computed: {
    loadPropertyUrl() {
      return `properties/${this.$route.params.id}`
    },

    loadCompetitorsUrl() {
      return `properties/${this.$route.params.id}/rooms/${this.$route.params.room_id}/competitors`
    },

    loadRoomUrl() {
      return `properties/${this.$route.params.id}/rooms/${this.$route.params.room_id}`
    },

    isNotEmptyObjects() {
      return !isEmpty(this.property) && !isEmpty(this.filters)
    },

    downloadCompetitorsUrl() {
      return `/properties/${this.$route.params.id}/rooms/${this.$route.params.room_id}/competitors/download`
    },

    findCompetitorUrl() {
      return `/properties/${this.$route.params.id}/rooms/${this.$route.params.room_id}/competitors/find_competitors`
    },

    printAllCompetitorsUrl() {
      return `/properties/${this.$route.params.id}/rooms/${this.$route.params.room_id}/competitors/print_competitors`
    },

    competitorsIds() {
      return this.competitors.map((el) => el.id)
    }
  },

  watch: {
    filters() {
      this.uniqKey++
    }
  },

  mounted() {
    this.loadProperty()
    this.loadRoom()
    this.loadCompetitors()
  },

  updated() {
    if (this.printCompetitorsMode) {
      this.printAndReset()
    }
  },

  methods: {
    isEmpty,

    loadProperty() {
      api.get(this.loadPropertyUrl)
        .then(({ data: { data }}) => this.property = data)
    },

    loadCompetitors() {
      this.loading = true
      let params = this.paramsForLoadCompetitors()

      api.get(this.loadCompetitorsUrl, { params })
        .then(({ data: { data: { competitors, pagination } } }) => {
          this.competitorsForMarketPrice = competitors
          this.competitors = competitors
          // TODO: uncomment and replace this.competitors after unblock grouping competitors card
          // this.competitors = this.splitCompetitorsByNumber(competitors)
          this.pagination = pagination
          this.setPaginationData()
          this.loading = false
        })
    },

    // TODO: uncomment after unblock grouping competitors card
    // splitCompetitorsByNumber(competitors) {
    //   const competitorsNumbers = uniq(competitors.map((competitor) => competitor.number))
    //   const byNumber = competitorsNumbers.map(number => filter(competitors, { number }))
    //   const result = this.renameCompetitorsIds(byNumber)

    //   return flatten(result)
    // },

    // renameCompetitorsIds(competitorsByNumber) {
    //   return competitorsByNumber.map((number) => {
    //     return number.map((competitor, index) => {
    //       if (index == 0) {
    //         return competitor
    //       } else {
    //         return { ...competitor, number: `${competitor.number}-${index}` }
    //       }
    //     })
    //   })
    // },

    paramsForLoadCompetitors() {
      let params = {
        page: this.pagination.current_page,
        filters: this.filters,
        order: this.sortingOrder,
        property_id: this.$route.params.id
      }
      return params
    },

    loadRoom() {
      api.get(this.loadRoomUrl)
        .then(({ data: { data }}) => this.room = data)
    },

    paginate(page) {
      this.competitorRow = -1
      this.pagination = { ...this.pagination, current_page: this.pageForPagination(page) }
      this.loadCompetitors()
    },

    setPaginationData() {
      this.$refs.pagination.setPaginationData(this.pagination)
    },

    applyFilters(filters) {
      this.competitorRow = -1
      this.filters = filters
      this.pagination = { ...this.pagination, current_page: 1 }
      this.loadCompetitors()
    },

    downloadCompetitors() {
      let params = { filters: this.filters, order: this.sortingOrder }

      api.post(this.downloadCompetitorsUrl, params, {
        responseType: 'arraybuffer'
      }).then(({ data, headers }) => {
        this.downloadXlsx(data, 'competitors.xlsx', headers['content-type'])
      })
    },

    printData() {
      this.loading = true
      this.loadAllCompetitorsAndPrint()
    },

    loadAllCompetitorsAndPrint() {
      const params = {
        page: this.pagination.current_page,
        filters: this.filters,
        order: this.sortingOrder,
        property_id: this.$route.params.id
      }

      api.get(this.printAllCompetitorsUrl, {
        params
      }).then(({ data: { data } }) => {
          this.competitors = data
          // TODO: uncomment and replace this.competitors after unblock grouping competitors card
          // this.competitors = this.splitCompetitorsByNumber(data)
      }).then(this.printCompetitors)
    },

    printCompetitors() {
      this.setupPrintAttributes()
      this.loading = false
      this.printCompetitorsMode = true
    },

    setupPrintAttributes() {
      this.tableHeight = "auto"
      this.marketPriceHeight = "auto"
    },

    printAndReset() {
      setTimeout(() => {
        this.printCompetitorsMode = false
        window.print()
        this.tableHeight = '600px'
        this.marketPriceHeight = '500px'
        this.loadCompetitors()
      }, 500)
    },

    sortingCompetitors(orderData) {
      this.competitorRow = -1
      this.sortingOrder = orderData
      this.pagination = { ...this.pagination, current_page: 1 }
      this.loadCompetitors()
    },

    pageForPagination(page) {
      switch (page) {
        case 'next':
          return Number(this.pagination.current_page + 1)
        case 'prev':
          return Number(this.pagination.current_page - 1)
        default:
          return page
      }
    },

    findCompetitor(number) {
      let params = this.paramsForLoadCompetitors()
      params = { ... params, number }
      api.get(this.findCompetitorUrl, { params }).then(({ data: { page, row }}) => {
        this.pagination = { ...this.pagination, current_page: page }
        this.competitorRow = row
        this.loadCompetitors()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.saving-backdrop {
  background-color: rgba(200, 200, 200, 0.6);
  bottom: 0;
  left: 0;
  padding-top: 20vh;
  position: absolute;
  right: 0;
  top: -10px;
  z-index: 10;
}

h3 {
  color: #7c91a7;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 0;
}

.title-path {
  a,
  span {
    color: #7c91a7;
    font-size: 11px;
    letter-spacing: 0;
  }

  a:hover {
    color: #7c91a7;
    text-decoration: none;
  }

  #arrow-delimiter {
    margin: 0 5px;
  }
}

.header-filters {
  background-color: #fff;
  border-bottom: 1px solid #e7ecf4;
}

.bordered-row {
  border-bottom: 1px solid #e7ecf4;
}

h1 {
  color: #313541;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0;
  opacity: 0.9;
}

.btn-action {
  background-color: transparent;
  border: 1px solid transparent;
  box-shadow: none !important;
  font-weight: 500;
  height: 36px;
  letter-spacing: 0;
  line-height: 34px;
  padding: 0 15px;

  &:hover {
    background-color: transparent;
  }

  &:active {
    border-color: transparent !important;
  }
}

.btn-download {
  color: #008bd0;
  font-size: 14px;

  &:hover {
    border: 1px solid transparent;
    color: #008bd0;
  }
}

.btn-group {
  .btn-action {
    border: 1px solid #dfe6ee;
    color: #313541;
    font-size: 12px;

    &.selected {
      background-image: linear-gradient(270deg, rgba(24, 172, 235, 0.98) 0%, #18a4eb 100%);
      border: 1px solid #18a4eb;
      color: #fff;

      &:hover {
        z-index: 1;
      }
    }

    &:hover {
      z-index: 0;
    }
  }
}

.reset-search {
  background-color: transparent;
  border: 0;
  color: #008bd0;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0;
  text-decoration: none;
}

.action-row {
  display: flex;
  justify-content: space-between;
}

@media print {
  .pagination-block,
  .page-print-version {
    display: none;
  }

  .property-competitors {
    color-adjust: exact;
  }

  ::v-deep .vuetable {
    td {
      &.ota-site {
        .hide-comparison-competitor {
          display: none;
        }
      }
    }
  }
}
</style>
